import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@hweeks/kei-costs-ui-testids';
import { useEffect, useRef, useState } from 'react';
import { SectionLabel } from '../../design';
import { KeiVan } from './illustrations';
import { RoadScene } from './illustrations/RoadScene';
import './jobs-carousel.css';
const SWIPE_PX = 48;
/**
 * The home page's "seven jobs": one job at a time, the copy on the left and the app excerpt
 * that proves it on the right, with a kei van driving a road along the foot of the section.
 * The road always scrolls (paused offscreen and under reduced motion); each job is a resting
 * spot for the van, left to right, so going forward it pulls ahead with a puff of exhaust and
 * going back it brakes and eases back.
 *
 * It's the WAI-ARIA tabbed carousel: the dots are tabs, each job a tabpanel. No auto-advance.
 * Arrow keys, Home and End work anywhere in it; swipes on touch. Every job's text is in the
 * markup (inactive ones are inert), so a prerendered page reads the same as before.
 */ export function JobsCarousel({ title, jobs }) {
    const [index, setIndex] = useState(0);
    // Which way the van last went, and how many moves so far: a new move remounts the van's
    // body so its pitch animation (and the puff) plays again.
    const [move, setMove] = useState({
        dir: null,
        n: 0
    });
    // Slides whose images may load: the one showing and the next, then any visited.
    const [ready, setReady] = useState(()=>new Set([
            0,
            1
        ]));
    const [announce, setAnnounce] = useState('');
    const [running, setRunning] = useState(false);
    const rootRef = useRef(null);
    const tabRefs = useRef([]);
    const swipe = useRef(null);
    const last = jobs.length - 1;
    // The road only moves while the section is on screen.
    useEffect(()=>{
        const el = rootRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;
        const io = new IntersectionObserver(([e])=>setRunning(e.isIntersecting), {
            rootMargin: '80px'
        });
        io.observe(el);
        return ()=>io.disconnect();
    }, []);
    const go = (next, opts = {})=>{
        const to = Math.max(0, Math.min(last, next));
        if (to === index) return;
        setMove((m)=>({
                dir: to > index ? 'fwd' : 'back',
                n: m.n + 1
            }));
        setIndex(to);
        setReady((r)=>new Set([
                ...r,
                to,
                Math.min(last, to + 1)
            ]));
        if (opts.announce) setAnnounce(`Job ${to + 1} of ${jobs.length}: ${jobs[to].title}`);
        if (opts.focusTab) tabRefs.current[to]?.focus();
    };
    const onKeyDown = (e)=>{
        const onTab = e.target.getAttribute('role') === 'tab';
        const keys = {
            ArrowRight: index + 1,
            ArrowLeft: index - 1,
            Home: 0,
            End: last
        };
        if (!(e.key in keys)) return;
        e.preventDefault();
        // On a dot, focus follows the selection (tabs pattern); elsewhere say where we went.
        go(keys[e.key], {
            focusTab: onTab,
            announce: !onTab
        });
    };
    return /*#__PURE__*/ _jsxs("section", {
        ref: rootRef,
        className: "mk-jobs mk-ground--sora",
        "aria-roledescription": "carousel",
        "aria-labelledby": "mk-jobs-title",
        "data-dir": move.dir ?? undefined,
        "data-running": running || undefined,
        "data-testid": Testids.app.landingPage.jobs,
        onKeyDown: onKeyDown,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mk-jobs__inner",
                children: [
                    /*#__PURE__*/ _jsx(SectionLabel, {
                        k: "製品",
                        className: "mk-jobs__label",
                        children: "What it does"
                    }),
                    /*#__PURE__*/ _jsx("h2", {
                        id: "mk-jobs-title",
                        className: "mk-jobs__title",
                        children: title
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        id: "mk-jobs-slides",
                        className: "mk-jobs__slides",
                        onPointerDown: (e)=>{
                            if (e.pointerType !== 'mouse') swipe.current = {
                                x: e.clientX,
                                y: e.clientY
                            };
                        },
                        onPointerUp: (e)=>{
                            const s = swipe.current;
                            swipe.current = null;
                            if (!s) return;
                            const dx = e.clientX - s.x;
                            if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(e.clientY - s.y)) {
                                go(index + (dx < 0 ? 1 : -1), {
                                    announce: true
                                });
                            }
                        },
                        onPointerCancel: ()=>{
                            swipe.current = null;
                        },
                        children: jobs.map((job, i)=>{
                            const active = i === index;
                            return /*#__PURE__*/ _jsxs("div", {
                                id: `mk-job-${i}`,
                                role: "tabpanel",
                                "aria-roledescription": "slide",
                                "aria-label": `${i + 1} of ${jobs.length}: ${job.title}`,
                                className: active ? 'mk-jobs__slide is-active' : 'mk-jobs__slide',
                                "aria-hidden": !active || undefined,
                                ...active ? {} : {
                                    inert: ''
                                },
                                "data-testid": Testids.app.landingPage.job(String(i + 1)),
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mk-jobs__copy",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "mk-jobs__no",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        children: [
                                                            "No. ",
                                                            String(i + 1).padStart(2, '0')
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "mk-jobs__k",
                                                        children: job.k
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "mk-jobs__h3",
                                                children: job.title
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "mk-jobs__text",
                                                children: job.text
                                            }),
                                            /*#__PURE__*/ _jsx("a", {
                                                href: job.href,
                                                className: "mk-jobs__more",
                                                children: "How it works →"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mk-jobs__excerpt",
                                        children: ready.has(i) ? job.excerpt : null
                                    })
                                ]
                            }, job.title);
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-jobs__controls",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                className: "mk-jobs__arrow",
                                "aria-label": "Previous job",
                                "aria-controls": "mk-jobs-slides",
                                "aria-disabled": index === 0 || undefined,
                                "data-testid": Testids.app.landingPage.jobsPrev,
                                onClick: ()=>go(index - 1, {
                                        announce: true
                                    }),
                                children: "←"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mk-jobs__dots",
                                role: "tablist",
                                "aria-label": "Jobs",
                                children: jobs.map((job, i)=>/*#__PURE__*/ _jsx("button", {
                                        ref: (el)=>{
                                            tabRefs.current[i] = el;
                                        },
                                        type: "button",
                                        role: "tab",
                                        className: "mk-jobs__dot",
                                        "aria-selected": i === index,
                                        "aria-controls": `mk-job-${i}`,
                                        "aria-label": job.title,
                                        tabIndex: i === index ? 0 : -1,
                                        "data-testid": Testids.app.landingPage.jobDot(String(i + 1)),
                                        onClick: ()=>go(i)
                                    }, job.title))
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                className: "mk-jobs__arrow",
                                "aria-label": "Next job",
                                "aria-controls": "mk-jobs-slides",
                                "aria-disabled": index === last || undefined,
                                "data-testid": Testids.app.landingPage.jobsNext,
                                onClick: ()=>go(index + 1, {
                                        announce: true
                                    }),
                                children: "→"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "rhd-sr-only",
                        "aria-live": "polite",
                        "aria-atomic": "true",
                        children: announce
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mk-road",
                style: {
                    ['--p']: index / last
                },
                children: [
                    /*#__PURE__*/ _jsx(RoadScene, {}),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mk-road__van",
                        children: [
                            move.dir === 'fwd' && /*#__PURE__*/ _jsxs("span", {
                                className: "mk-road__puff",
                                "aria-hidden": "true",
                                children: [
                                    /*#__PURE__*/ _jsx("i", {}),
                                    /*#__PURE__*/ _jsx("i", {}),
                                    /*#__PURE__*/ _jsx("i", {})
                                ]
                            }, `puff-${move.n}`),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mk-road__pitch",
                                children: /*#__PURE__*/ _jsx(KeiVan, {})
                            }, `van-${move.n}`)
                        ]
                    })
                ]
            })
        ]
    });
}
